import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IMovementScheduleAccount,
    IMovementScheduleEntries,
    IMovementScheduleFli,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IMovementScheduleEditableColumnHeaders } from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { MovementScheduleColumnIndexes } from '../../../enums/movementScheduleColumnIndexes.enum';
import { MovementScheduleHelper } from '../../../helpers/movementSchedule.helper';
import { IMovementScheduleDates } from '../../../interfaces/movement-schedule/IMovementScheduleDates.interface';
import { IMovementScheduleTable } from '../../../interfaces/movement-schedule/IMovementScheduleTable.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:MovementScheduleTable');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-movements-schedule-table',
    templateUrl: 'movement-schedule-table.component.html',
    styleUrls: ['movement-schedule-table.component.scss'],
})
export class MovementScheduleTableComponent implements OnInit {
    movementScheduleTableForm: UntypedFormGroup;
    viewModes = ViewMode;
    rowsHighlight: boolean[] = [];
    footnotes: string[] = [];
    currentFootnoteValue: string = '';

    @Input() dates: IMovementScheduleDates | undefined;
    private _reportLocale: string;

    private _mode: ViewMode;
    currentMode: ViewMode;

    openDate: string;
    closeDate: string;

    data: IMovementScheduleAccount[] = [];

    @Input() defaultLanguage: string | undefined;

    @Input()
    set reportLocale(value: string) {
        if (value != null) {
            this._reportLocale = value;
            this.reinitializeHeadersFormGroup();
            this.handleTableData();
            if (this._fliData && this._fliData.caption) {
                this.selectedFliName =
                    this._fliData.caption[this.reportLocale] || this._fliData.caption[this.defaultLanguage];
            }
        }
    }

    get reportLocale(): string {
        return this._reportLocale;
    }

    private _fliData: IMovementScheduleFli;
    @Input() set fliData(value: IMovementScheduleFli) {
        if (value != null) {
            this._fliData = cloneDeep<IMovementScheduleFli>(value);
            this.reinitializeHeadersFormGroup();
            this.handleTableData();
            this.selectedFliName =
                this._fliData.caption?.[this.reportLocale] || this._fliData.caption?.[this.defaultLanguage];
        }
    }

    @Input()
    set mode(value: ViewMode) {
        this._mode = value;
    }
    get mode(): ViewMode {
        return this._mode;
    }

    selectedFliName: string | undefined;

    @Input() highlightTodos = false;

    @Input() tableNumber: string | undefined;

    @Input() editableData: boolean = true;

    private _hasFootnotes: boolean = false;
    @Input() 
    get hasFootnotes(): boolean {
        return this._hasFootnotes;
    }
    set hasFootnotes(value: boolean) {
        this._hasFootnotes = value;
    }

    @Input() set savedFootnote(value: string | undefined) {
        if (value !== undefined) {
            this.currentFootnoteValue = value || '';
            this.footnotes = value ? [value] : [];

            // Update form control if it exists
            if (this.movementScheduleTableForm?.get('footnote')) {
                this.movementScheduleTableForm
                    .get('footnote')
                    .setValue(this.currentFootnoteValue, { emitEvent: false });
            }
        }
    }

    @Output() movementScheduleTableData = new EventEmitter<IMovementScheduleTable>();

    constructor(
        private formBuilder: UntypedFormBuilder,
        private movementScheduleHelper: MovementScheduleHelper,
        private i18nextService: I18NextService,
    ) {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.movementScheduleTableForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.handleFootnoteChanges();
            this.emitTableData();
        });

        this.handleTotalText(this._fliData.totalRow, this.reportLocale);
    }

    private handleFootnoteChanges(): void {
        const footnoteValue = this.movementScheduleTableForm.value.footnote;
        if (footnoteValue != null && footnoteValue !== '') {
            this.footnotes = [footnoteValue];
            this.currentFootnoteValue = footnoteValue;
        } else {
            this.footnotes = [];
            this.currentFootnoteValue = '';
        }
    }

    private applyLocalizations(locale: string): void {
        if (
            this.movementScheduleTableForm != null &&
            this.movementScheduleTableForm.get('totalRow')['controls']['text']['value'] === ''
        ) {
            this.handleTotalText(null, locale);
        }
    }

    initializeForm() {
        this.movementScheduleTableForm = this.formBuilder.group({
            headers: this.formBuilder.group({
                header1: this.formBuilder.group(this.initializeDynamicObject('column1')),
                header2: this.formBuilder.group(this.initializeDynamicObject('column2')),
                header3: this.formBuilder.group(this.initializeDynamicObject('column3')),
                header4: this.formBuilder.group(this.initializeDynamicObject('column4')),
            }),
            dataSource: this.formBuilder.array([]),
            totalRow: this.formBuilder.group({
                closeValue: [''],
                column1: [''],
                column2: [''],
                column3: [''],
                column4: [''],
                openValue: [''],
                text: [''],
            }),
            footnote: new UntypedFormControl(this.currentFootnoteValue || ''),
        });
        this.movementScheduleTableForm.setControl('dataSource', this.getAllAsFormArray());
    }

    private addFormGroup(dataSource: IMovementScheduleAccount): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(dataSource[key]);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.data.map(this.addFormGroup);
        return new UntypedFormArray(formGroups);
    }

    private handleTableData(): void {
        if (this._fliData != null && this._reportLocale != null) {
            this.applyLocalizations(this._reportLocale);

            this.setDates(this._fliData.templateType);

            this.setEditableHeaderValues(this._fliData.headers);

            this.data = cloneDeep<IMovementScheduleAccount[]>(this._fliData.accounts);

            this.setDataSource();

            this.applyHighlightOfRows(this._fliData.accounts);

            this.handleTotalText(this._fliData.totalRow, this.reportLocale);
        }
    }

    private handleTotalText(value: IMovementScheduleEntries, locale: string) {
        if (value == null) {
            this.handleTotalRowTranslationText(
                this.i18nextService.t('aumico_notes_table_with_form_total', { lng: locale }) as string,
            );
        } else {
            this.handleTotalRowTranslationText(value.text);
        }
    }

    private handleTotalRowTranslationText(translation: string) {
        this.movementScheduleTableForm.patchValue({
            totalRow: {
                text: translation,
            },
        });
    }

    private handleRowChangeInForm(rowChange: IMovementScheduleAccount, index: number) {
        const shouldRowBeHighlighted = this.shouldHighlightRow(rowChange);

        this.rowsHighlight[index] = shouldRowBeHighlighted;

        this.movementScheduleTableForm.patchValue({
            totalRow: {
                column1: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.FIRST_COLUMN,
                    this.movementScheduleTableForm.getRawValue()['dataSource'],
                ),
                column2: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.SECOND_COLUMN,
                    this.movementScheduleTableForm.getRawValue()['dataSource'],
                ),
                column3: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.THIRD_COLUMN,
                    this.movementScheduleTableForm.getRawValue()['dataSource'],
                ),
                column4: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.FORTH_COLUMN,
                    this.movementScheduleTableForm.getRawValue()['dataSource'],
                ),
            },
        });
    }
    private reinitializeHeadersFormGroup() {
        this.movementScheduleTableForm.setControl(
            'headers',
            this.formBuilder.group({
                header1: this.formBuilder.group(this.initializeDynamicObject('column1')),
                header2: this.formBuilder.group(this.initializeDynamicObject('column2')),
                header3: this.formBuilder.group(this.initializeDynamicObject('column3')),
                header4: this.formBuilder.group(this.initializeDynamicObject('column4')),
            }),
        );
    }

    private setDataSource() {
        this.movementScheduleTableForm.setControl('dataSource', this.getAllAsFormArray());

        this.movementScheduleTableForm.patchValue({
            totalRow: {
                closeValue: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.CLOSE_VALUE,
                    this.data,
                ),
                column1: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.FIRST_COLUMN,
                    this.data,
                ),
                column2: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.SECOND_COLUMN,
                    this.data,
                ),
                column3: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.THIRD_COLUMN,
                    this.data,
                ),
                column4: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.FORTH_COLUMN,
                    this.data,
                ),
                openValue: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.OPEN_VALUE,
                    this.data,
                ),
            },
        });

        (this.movementScheduleTableForm.controls['dataSource'] as UntypedFormArray).controls.forEach((row, index) => {
            const mutatedRow = row as UntypedFormGroup;
            row.valueChanges
                .pipe(untilDestroyed(this))
                .subscribe((rowChange) => this.handleRowChangeInForm(mutatedRow.getRawValue(), index));
        });
    }

    applyHighlightOfRows(value: IMovementScheduleAccount[]) {
        this.rowsHighlight = [];
        value.forEach((row, index) => {
            const shouldRowBeHighlighted = this.shouldHighlightRow(row);
            this.rowsHighlight[index] = shouldRowBeHighlighted;
        });
    }

    private setEditableHeaderValues(headers: IMovementScheduleEditableColumnHeaders) {
        if (headers != null) {
            this.movementScheduleTableForm.patchValue({
                headers: {
                    header1: headers.column1,
                    header2: headers.column2,
                    header3: headers.column3,
                    header4: headers.column4,
                },
            });
        }
    }

    private initializeDynamicObject(key: string) {
        const formGroup = {};

        formGroup[this.reportLocale] = this._fliData?.headers[key][this.reportLocale];
        return formGroup;
    }

    private emitTableData() {
        const tableFormValues = this.movementScheduleTableForm.getRawValue();

        const tableData = this.movementScheduleHelper.prepareMovementScheduleFormData(
            tableFormValues,
            this._fliData?.headers,
        );
        tableData.footnotes = this.footnotes;

        this.movementScheduleTableData.emit(tableData);
    }

    private setDates(templateType: TemplateType): void {
        if (this.dates == null) {
            return;
        }

        if (templateType === TemplateType.INCOME) {
            this.openDate = this.dates.incomeOpenDate;
            this.closeDate = this.dates.incomeCloseDate;
        } else {
            this.openDate = this.dates.balanceOpenDate;
            this.closeDate = this.dates.balanceCloseDate;
        }
    }

    private shouldHighlightRow(row: IMovementScheduleAccount): boolean {
        const closeValue = NumberUtils.roundNumber(row.closeValue, true);

        const sumOfOpenAndOtherColumnsExceptCloseValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [row.column1, row.column2, row.column3, row.column4, row.openValue],
            row.openValue,
            true,
        );

        return !NumberUtils.areNumbersEqual(sumOfOpenAndOtherColumnsExceptCloseValue, closeValue);
    }
}
